import { GST_RATE, gstOn } from "@/lib/offer";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Trash2, Loader2, ShoppingCart } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/Field";
import { PaymentMethodPicker, usePaymentConfig } from "@/components/PaymentMethod";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { api, fmtINR } from "@/lib/api";
import { RazorpayPay } from "@/components/admission/RazorpayPay";
import { PolicyConsent } from "@/components/PolicyConsent";

export const CartSheet = () => {
  const { items, update, remove, total, count } = useCart();
  const { L } = useLang();
  const nav = useNavigate();
  const cfg = usePaymentConfig();
  const [method, setMethod] = useState("razorpay");
  const [order, setOrder] = useState(null);
  useEffect(() => { if (cfg.razorpay_enabled) setMethod("razorpay"); }, [cfg.razorpay_enabled]);
  const [c, setC] = useState({ customer_name: "", customer_email: "", customer_phone: "", shipping_address: "", robox_referral: "" });
  const [agreed, setAgreed] = useState(false);
  const hasRobox = items.some((i) => i.item_id === "prod-robox-app");
  const [loading, setLoading] = useState(false);
  const on = (k) => (e) => setC({ ...c, [k]: e.target.value });

  const checkout = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...c, accepted_policies: hasRobox && agreed, origin_url: window.location.origin, items: items.map(({ item_id, kind, quantity }) => ({ item_id, kind, quantity })) };
      const { data } = await api.post("/razorpay/shop", payload);
      setOrder(data); setLoading(false);
    } catch (err) {
      if (err.message !== "dismissed") toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || L("Checkout failed", "चेकआउट विफल"));
      setLoading(false);
    }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button className="rounded-full bg-white border border-[#00E5FF]/40 hover:bg-[#FF3B3B] hover:border-[#FF3B3B] text-[#0A192F] hover:text-white" data-testid="open-cart-btn">
          <ShoppingCart className="w-4 h-4 mr-2" /> {L("Cart", "कार्ट")} ({count}) · {fmtINR(total)}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto bg-white" data-testid="cart-sheet">
        <SheetHeader><SheetTitle>{L("Your Cart", "आपका कार्ट")}</SheetTitle></SheetHeader>
        {items.length === 0 ? <p className="mt-8 text-slate-500" data-testid="cart-empty">{L("Your cart is empty.", "आपका कार्ट खाली है।")}</p> : (
          <>
            <ul className="mt-6 divide-y">
              {items.map((i) => (
                <li key={i.kind + i.item_id} className="py-3 flex gap-3 items-center" data-testid={`cart-item-${i.item_id}`}>
                  <div className="flex-1 min-w-0"><div className="font-semibold text-sm truncate">{i.name}</div><div className="text-xs text-slate-500">{fmtINR(i.unit_price_inr)}</div></div>
                  <div className="flex items-center border rounded-md">
                    <button type="button" className="p-1.5" onClick={() => update(i.item_id, i.kind, i.quantity - 1)} data-testid={`qty-dec-${i.item_id}`}><Minus className="w-3 h-3" /></button>
                    <span className="w-6 text-center text-sm" data-testid={`qty-${i.item_id}`}>{i.quantity}</span>
                    <button type="button" className="p-1.5" onClick={() => update(i.item_id, i.kind, i.quantity + 1)} data-testid={`qty-inc-${i.item_id}`}><Plus className="w-3 h-3" /></button>
                  </div>
                  <button type="button" onClick={() => remove(i.item_id, i.kind)} className="p-1.5 text-slate-500 hover:text-red-600" data-testid={`remove-${i.item_id}`}><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
            <div className="flex justify-between text-sm mt-4 border-t pt-3 text-slate-600"><span>{L("Subtotal", "उप-योग")}</span><span data-testid="cart-subtotal">{fmtINR(total)}</span></div>
            <div className="flex justify-between text-sm text-slate-600"><span>GST @ {GST_RATE}%</span><span data-testid="cart-gst">+ {fmtINR(gstOn(total))}</span></div>
            <div className="flex justify-between font-bold text-lg mt-2"><span>{L("Total", "कुल")}</span><span data-testid="cart-total">{fmtINR(total + gstOn(total))}</span></div>
            <form onSubmit={checkout} className="mt-6 space-y-3" data-testid="checkout-form">
              <Field id="customer_name" label={L("Full name", "पूरा नाम")} required value={c.customer_name} onChange={on("customer_name")} />
              <Field id="customer_email" label={L("Email", "ईमेल")} type="email" required value={c.customer_email} onChange={on("customer_email")} />
              <Field id="customer_phone" label={L("Phone", "फ़ोन")} value={c.customer_phone} onChange={on("customer_phone")} />
              <Field id="shipping_address" label={L("Shipping address", "डिलीवरी पता")} value={c.shipping_address} onChange={on("shipping_address")} />
              {items.some((i) => i.item_id === "prod-robox-app") && <Field id="robox_referral" label={L("Friend's Robo-X licence code (optional) — they get 7 free days", "दोस्त का Robo-X लाइसेंस कोड (वैकल्पिक) — उन्हें 7 दिन मुफ़्त")} placeholder="RX-XXXX-XXXX-XXXX" maxLength={24} value={c.robox_referral} onChange={on("robox_referral")} />}
              <PaymentMethodPicker value={method} onChange={setMethod} razorpayEnabled={cfg.razorpay_enabled} />
              {hasRobox && <PolicyConsent checked={agreed} onChange={setAgreed} testid="cart-policy-consent" />}
              <Button type="submit" disabled={loading || !cfg.razorpay_enabled || (hasRobox && !agreed)} className="w-full rs-cta h-12 rounded-full" data-testid="checkout-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : L("Pay with UPI QR / Card / Net banking", "UPI QR / कार्ड / नेट बैंकिंग से भुगतान करें")}
              </Button>
            </form>
          </>
        )}
        <RazorpayPay order={order} description="Roboshop order" onPaid={(d) => { setOrder(null); nav(`/payment/success?session_id=${d.session_id}`); }} onClose={() => setOrder(null)} />
      </SheetContent>
    </Sheet>
  );
};
